/**
 * Dial — a ring gauge: a dotted track with an arc swept clockwise from twelve
 * o'clock, labelled beneath it.
 *
 * Corpus: `PR:242-282`, a four-column grid of eight tiles.
 * `:244` is the mark — an 80×80 viewBox capped at 84px, a 6px dotted track at
 * r=34 with a `1 6.4` dasharray, a 3.5px round-capped arc at r=26 rotated
 * `-90 40 40`, and an r=2 centre dot — and `:245-246` are the two label lines.
 * None of that geometry has a token; it is disclosed in `dial.css` beside the
 * rules that carry it, and no token is raised for it here.
 *
 * This is the one component whose only source is the dark landing page, so
 * every colour is re-derived for the light product rather than carried across
 * (the light-only decision): the arc's `--accL` `#4fbe86` (`:31`) becomes `--rp-green`, the
 * `rgba(244,241,230,0.16)` track becomes `--rp-border-divider-2`, the
 * `rgba(244,241,230,0.5)` centre dot becomes `--rp-ink-muted`, and the
 * `rgba(244,241,230,0.42)` sublabel becomes `--rp-ink-secondary`.
 *
 * The corpus's accessibility is not ported either, because it is wrong: `:244`
 * marks the svg `aria-hidden`, gives it no role and no name, renders no number
 * anywhere, and leaves the labels as unassociated divs — so the value reaches
 * sighted readers only. Here the tile is one `role="img"` named from the
 * caller's strings, and `valueText` also renders as visible text, so neither
 * the arc's length nor its colour ever carries the value alone.
 *
 * Every string a person reads is a prop. `value` is a fraction and
 * never a duration: the corpus's arc lengths are per-jurisdiction tribunal
 * timelines, which are statutory claims, and those belong to jurisdiction-gated
 * data rather than to a default or a fixture (the jurisdiction rule).
 */
export interface DialProps {
  /** Proportion of the ring to sweep, 0 to 1. Anything outside is clamped. */
  readonly value: number;
  /** COPY. The primary line under the ring. */
  readonly label: string;
  /**
   * COPY. An optional second line; the corpus draws a tribunal acronym here.
   *
   * `| undefined` is explicit because `exactOptionalPropertyTypes` is on
   * repo-wide: without it a caller reading the line out of optional data —
   * `sublabel={tribunal?.acronym}` — does not compile, which is the shape every
   * real call site has. The `?? ''` variant of that same call site is treated
   * as absent rather than as an empty line.
   */
  readonly sublabel?: string | undefined;
  /** COPY. The value stated in words, e.g. "6 of 9 photos uploaded". */
  readonly valueText: string;
  /** Stagger index within a group of dials. Presentational only. */
  readonly revealDelay?: number | undefined;
}

const ARC_RADIUS = 26;

/**
 * 2π × 26. `PR:244` rounds this to `163px` and writes each
 * tile as an arc length out of it, so a jurisdiction's `69` is 69/163. The
 * unrounded figure is used instead so that `value: 1` closes the ring; the
 * sweep's first frame repeats it in `dial.css`, and the two move together.
 */
const CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;

export function Dial({ value, label, sublabel, valueText, revealDelay }: DialProps) {
  // A caller computing `done / total` hands over NaN the moment `total` is 0,
  // and NaN reaches the DOM as an invalid dashoffset that browsers resolve to
  // zero — a fully swept ring, which is the most wrong answer available.
  const swept = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
  const index = revealDelay ?? 0;
  // A negative delay starts a CSS animation part-way through, so an off-by-one
  // in a caller's index would land the reveal already swept.
  const stagger = Number.isFinite(index) ? Math.max(index, 0) : 0;
  // One clock for both animations: `dial.css` fades the tile and sweeps the
  // arc, and a stagger applied to only one of them tears them apart.
  const delay = `calc(var(--rp-duration-fast) * ${stagger})`;
  // An empty string is a caller with nothing to put on the second line, not a
  // second line that is empty: `sublabel={tribunal?.acronym ?? ''}` is the call
  // that produces one. Both the markup and the name below read this rather than
  // the prop, so an empty span cannot appear under a name that omits it.
  const secondLine = sublabel === '' ? undefined : sublabel;
  // Joined on a space rather than on a comma: a comma is Latin punctuation and
  // this name is read in four languages, one of which writes its own.
  const name = [label, secondLine, valueText].filter(Boolean).join(' ');

  return (
    <div className="rp-dial" role="img" aria-label={name} style={{ animationDelay: delay }}>
      <svg className="rp-dial__ring" viewBox="0 0 80 80" aria-hidden="true">
        <circle
          className="rp-dial__track"
          cx="40"
          cy="40"
          r="34"
          fill="none"
          strokeWidth={6}
          strokeDasharray="1 6.4"
        />
        {/* Twelve o'clock, clockwise, in every locale: elapsed time does not
            mirror, so this rotation is deliberately not flipped under `rtl`
            and only the label block follows the writing direction. */}
        <circle
          className="rp-dial__arc"
          cx="40"
          cy="40"
          r={ARC_RADIUS}
          fill="none"
          strokeWidth={3.5}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
          strokeDasharray={CIRCUMFERENCE.toFixed(3)}
          strokeDashoffset={(CIRCUMFERENCE * (1 - swept)).toFixed(3)}
          style={{ animationDelay: delay }}
        />
        <circle className="rp-dial__dot" cx="40" cy="40" r="2" />
      </svg>
      <span className="rp-dial__label">{label}</span>
      {secondLine !== undefined && <span className="rp-dial__sublabel">{secondLine}</span>}
      <span className="rp-dial__value">{valueText}</span>
    </div>
  );
}
